import { SPRating } from '@moment/trading-types/reference-data'

export function getAllSpRatingsGreaterThanOrEqualTo(
  minRating: SPRating | null,
): SPRating[] {
  if (!minRating) {
    return []
  }
  const allRatings = Array.from(Object.values(SPRating))
  const minRatingIndex = allRatings.indexOf(minRating)
  return allRatings.slice(0, minRatingIndex + 1)
}

export function getAllSpRatingsLessThan(
  maxRating: SPRating | null,
): SPRating[] {
  if (!maxRating) {
    return []
  }
  const allRatings = Array.from(Object.values(SPRating))
  const allRatingsGTE = getAllSpRatingsGreaterThanOrEqualTo(maxRating)
  return allRatings.filter((rating) => !allRatingsGTE.includes(rating))
}

// Returns -1 if beforeRating is less than afterRating, 0 if they are equal, and 1 if beforeRating is greater than afterRating
export function compareSpRatings(
  beforeRating: SPRating | null,
  afterRating: SPRating | null,
): -1 | 0 | 1 {
  if (beforeRating === afterRating) return 0
  if (!beforeRating || !afterRating) return 0

  const allRatings = Array.from(Object.values(SPRating))
  const beforeIndex = allRatings.indexOf(beforeRating)
  const afterIndex = allRatings.indexOf(afterRating)

  if (beforeIndex === afterIndex) return 0
  return beforeIndex < afterIndex ? -1 : 1
}

export function convertSpRatingToNumber(rating: string) {
  return Object.values(SPRating)
    .reverse()
    .indexOf(rating as SPRating)
}

export function convertNumberToSpRating(number: number) {
  return Object.values(SPRating).reverse()[number]
}

// Rating utility functions for slider implementation
// These exclude NR and provide a more intuitive mapping for sliders

/**
 * Get all S&P ratings excluding NR (Not Rated) for slider use
 * Order: D (worst) to AAA (best) - reversed for intuitive slider behavior
 */
export function getSliderSpRatings(): SPRating[] {
  return Object.values(SPRating)
    .filter((rating) => rating !== SPRating.NR)
    .reverse()
}

/**
 * Convert S&P rating to slider value (0 = D, 21 = AAA)
 * Returns -1 if rating not found or is NR
 */
export function convertSpRatingToSliderValue(rating: SPRating): number {
  const sliderRatings = getSliderSpRatings()
  return sliderRatings.indexOf(rating)
}

/**
 * Convert slider value to S&P rating (0 = D, 21 = AAA)
 * Returns null if index out of bounds
 */
export function convertSliderValueToSpRating(value: number): SPRating | null {
  const sliderRatings = getSliderSpRatings()
  return sliderRatings[value] || null
}

/**
 * Get min/max slider bounds for S&P ratings
 */
export function getSpRatingSliderBounds() {
  const sliderRatings = getSliderSpRatings()
  return {
    min: 0, // D (worst)
    max: sliderRatings.length - 1, // AAA (best)
  }
}

/**
 * Parse user input string and convert to slider value
 * Accepts rating strings like "AAA", "BB+", "D", etc.
 * Returns the numerical slider value or 0 if invalid
 */
export function parseSpRatingInput(input: string): number {
  // Clean and normalize the input
  const normalizedInput = input.trim().toUpperCase()

  // Check if input matches any S&P rating
  const matchedRating = Object.values(SPRating).find(
    (rating) => rating.toUpperCase() === normalizedInput,
  )

  if (matchedRating && matchedRating !== SPRating.NR) {
    return convertSpRatingToNumber(matchedRating)
  }

  // If no match, return 0 (default to lowest rating)
  return 0
}
